import { Building2, HeartHandshake, CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useSettings } from "@/context/SettingsContext";
import { Reveal } from "@/components/Reveal";

export const useHistory = () => {
  const { t } = useLanguage();
  const { settings } = useSettings();
  const h = settings.content?.history || {};
  return {
    ...t.history,
    intro: h.intro || t.history.intro,
    mission: h.mission || t.history.mission,
    milestones: h.milestones?.length ? h.milestones : t.history.milestones,
    facts: h.facts?.length ? h.facts : t.history.facts,
  };
};

const HistorySection = () => {
  const h = useHistory();
  return (
    <section className="relative overflow-hidden bg-white py-24 sm:py-32" data-testid="history-section">
      <div className="dots-bg pointer-events-none absolute inset-0 opacity-40" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="history-facts">
                {h.facts.map((f, i) => (
                  <div key={i} className="rounded-2xl border border-brand/10 bg-mint/70 p-4" data-testid={`history-fact-${i}`}>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand">{f.label}</p>
                    <p className="font-display mt-1 text-base font-bold text-forest sm:text-lg">{f.value}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 rounded-3xl bg-brand p-8 text-white shadow-2xl shadow-brand/20" data-testid="history-mission">
                <p className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-white/80"><HeartHandshake className="h-4 w-4 text-gold" />{h.missionTitle}</p>
                <p className="mt-4 text-base leading-relaxed sm:text-lg">{h.mission}</p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <ol className="relative border-l-2 border-brand/15 pl-8" data-testid="history-timeline">
              {h.milestones.map((m, i) => (
                <li key={i} className="relative pb-10 last:pb-0" data-testid={`history-milestone-${i}`}>
                  <span className={`absolute -left-[2.45rem] top-1 flex h-7 w-7 items-center justify-center rounded-full border-4 border-white shadow ${i % 2 === 0 ? "bg-gold" : "bg-brand"}`}><Building2 className="h-3 w-3 text-white" /></span>
                  <Reveal delay={0.1 * i}>
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold">{m.year}</p>
                    <h3 className="font-display mt-1 text-xl font-bold text-forest sm:text-2xl">{m.title}</h3>
                    <p className="mt-2 max-w-2xl text-sm leading-relaxed text-brand-hover sm:text-base">{m.text}</p>
                  </Reveal>
                </li>
              ))}
            </ol>
            <Reveal delay={0.2}>
              <div className="mt-12 rounded-3xl border border-gold/20 bg-cream p-8" data-testid="history-charter">
                <p className="font-mono text-xs uppercase tracking-[0.3em] text-gold">{h.charterTitle}</p>
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {h.charter.map((c, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-forest sm:text-base"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand" />{c}</li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HistorySection;
